import { Button } from '@base-ui/react/button';
import {
  faBookOpen,
  faFileLines,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import { apiErrorDetail } from '@/api/errorDetail';
import type { components } from '@/api/qraft/schema';
import { ConfirmDialog } from '~/components/ui/ConfirmDialog';
import { CourseHeader, CourseOutlineLayout } from '~/components/ui/ContentNav';
import { DraftAncestorNotice } from '~/components/ui/DraftAncestorNotice';
import {
  DetailPage,
  EditorLayout,
  EditorMain,
  EditorPanel,
  FormSection,
  PanelGroup,
  PanelLabel,
  SaveGroup,
} from '~/components/ui/Editor';
import { useEditMode, validateEditSearch } from '~/components/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import {
  ErrorText,
  FieldHint,
  FormLabel,
  FormRow,
  InfoNotice,
  Select,
  TextInput,
  WarningNotice,
} from '~/components/ui/Form';
import { Frame, MediaPicker } from '~/components/ui/MediaPicker';
import { RichContent } from '~/components/ui/RichContent';
import { RichTextEditor } from '~/components/ui/RichTextEditor';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import { useUnsavedChanges } from '~/components/ui/useUnsavedChanges';
import {
  EditConflictNotice,
  editConflict,
  invalidateCourseVersions,
  LiveEditNotice,
} from '~/components/ui/Versions';
import buttonStyles from '~/styles/button.module.css';
import { CertificateTemplateControl } from './CertificateTemplateControl';

type CourseAdmin = components['schemas']['CourseAdmin'];

function invalidateCourse(courseId: number, version: number) {
  queryClient.invalidateQueries({
    queryKey: api.adminGetCourse.getQueryKey({
      path: { course_id: courseId },
      query: { version },
    }),
  });
  queryClient.invalidateQueries({
    queryKey: api.adminListCourses.getQueryKey({}),
  });
}

export const Route = createFileRoute('/_admin/courses/$courseId/$version')({
  component: CourseDetail,
  validateSearch: validateEditSearch,
});

function CourseDetail() {
  const { courseId, version } = Route.useParams();
  const courseIdNum = Number(courseId);
  const versionNum = Number(version);
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  // Admin-only read: the student course_detail is gated to a fully published
  // path, so a draft course would 404 there.
  const admin = api.adminGetCourse.useQuery({
    path: { course_id: courseIdNum },
    query: { version: versionNum },
  });

  if (admin.isLoading) return <Message>Loading course...</Message>;
  if (admin.error || !admin.data)
    return <Message $error>Error loading course</Message>;

  const course = admin.data;
  const certificateTitle = course.certificate_template_title;

  return (
    <DetailPage>
      <CourseHeader
        course={course}
        nav={!editing}
        action={
          !editing && (
            <Button className={buttonStyles.Button} onClick={startEditing}>
              Edit
            </Button>
          )
        }
      />

      {editing ? (
        <CourseEditor
          key={`${course.id}-${course.version}`}
          course={course}
          onDone={stopEditing}
        />
      ) : (
        <CourseOutlineLayout course={course}>
          <Section>
            <SectionTitle>Overview</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>ID</FieldLabel>
                <FieldValue>{course.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Version</FieldLabel>
                <FieldValue>{course.version}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Language</FieldLabel>
                <FieldValue>{course.language ?? '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Slug</FieldLabel>
                <FieldValue>{course.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Order</FieldLabel>
                <FieldValue>{course.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Status</FieldLabel>
                <FieldValue>{course.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Study Helper Required</FieldLabel>
                <FieldValue>
                  {course.study_helper_required ? 'Yes' : 'No'}
                </FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>Description</SectionTitle>
            {course.content ? (
              <Description
                // biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored course HTML
                dangerouslySetInnerHTML={{ __html: course.content }}
              />
            ) : (
              <Empty>No description yet.</Empty>
            )}
          </Section>

          <Section>
            <SectionTitle>Images</SectionTitle>
            <Images>
              <Field>
                <FieldLabel as="div">Cover image</FieldLabel>
                {course.cover_image ? (
                  <Frame $wide>
                    <img
                      src={course.cover_image}
                      alt={`${course.title} cover`}
                    />
                  </Frame>
                ) : (
                  <FieldValue>No cover image yet.</FieldValue>
                )}
              </Field>
              <Field>
                <FieldLabel as="div">Thumbnail</FieldLabel>
                {course.thumbnail ? (
                  <Frame>
                    <img
                      src={course.thumbnail}
                      alt={`${course.title} thumbnail`}
                    />
                  </Frame>
                ) : (
                  <FieldValue>No thumbnail yet.</FieldValue>
                )}
              </Field>
            </Images>
          </Section>

          <Section>
            <SectionTitle>Certificate</SectionTitle>
            <CertificateRow>
              <div>
                <FieldValue>
                  {certificateTitle !== null ? (
                    <>
                      Students who complete this course earn{' '}
                      <CertificateName>{certificateTitle}</CertificateName>.
                    </>
                  ) : (
                    <>No certificate. Completing this course earns nothing.</>
                  )}
                </FieldValue>
              </div>
              <CertificateTemplateControl
                owner={{ type: 'course', id: course.id }}
                hasTemplate={certificateTitle !== null}
                defaultTitle={course.title}
                onChanged={() => invalidateCourse(course.id, course.version)}
              />
            </CertificateRow>
          </Section>
        </CourseOutlineLayout>
      )}
    </DetailPage>
  );
}

function CourseEditor({
  course,
  onDone,
}: {
  course: CourseAdmin;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(course.title);
  const [slug, setSlug] = useState(course.slug);
  const [order, setOrder] = useState(String(course.order));
  const [status, setStatus] = useState(course.status);
  const [content, setContent] = useState(course.content);
  const [thumbnail, setThumbnail] = useState({
    key: course.thumbnail_key,
    url: course.thumbnail,
  });
  const [cover, setCover] = useState({
    key: course.cover_image_key,
    url: course.cover_image,
  });
  // One flag per picker: either one uploading holds the save.
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const uploading = thumbnailUploading || coverUploading;
  const [confirming, setConfirming] = useState(false);

  const formValues = JSON.stringify([
    title,
    slug,
    order,
    status,
    content,
    thumbnail.key,
    cover.key,
  ]);
  const [savedValues] = useState(formValues);
  const dirty = formValues !== savedValues;
  const {
    allowLeave,
    confirmDiscard,
    dialog: unsavedDialog,
  } = useUnsavedChanges(dirty);

  const invalidate = () => {
    invalidateCourse(course.id, course.version);
    invalidateCourseVersions(course.id);
  };

  const updateMutation = api.adminUpdateCourse.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      invalidate();
      onDone();
    },
  });

  const trimmedTitle = title.trim();
  // A published version is frozen structurally: order drives next-course
  // progression and slug is a live student URL, so the server rejects changing
  // them (or unpublishing). Those changes go in a new version.
  const isPublished = course.status === 'published';
  // A draft of a live course goes live from Review changes, not from here.
  const isDraftVersion = !isPublished && course.live_version != null;
  const willPublish = !isPublished && status === 'published';

  // Pinned when editing starts: a refetch (e.g. on tab focus) must not advance it,
  // or a save over someone else's change would slip past the conflict check.
  const [baseRevision] = useState(course.revision);
  const handleSave = () => {
    if (!trimmedTitle || updateMutation.isPending || uploading) return;
    if (willPublish && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    updateMutation.mutate({
      path: { course_id: course.id },
      query: { version: course.version },
      body: {
        title: trimmedTitle,
        slug,
        order: Number(order),
        status,
        // Only when edited, so other saves don't re-store the description.
        ...(content !== course.content ? { content } : {}),
        thumbnail_key: thumbnail.key,
        cover_image_key: cover.key,
        expected_revision: baseRevision,
      },
    });
  };

  const reload = () => {
    allowLeave();
    invalidate();
    onDone();
  };

  return (
    <EditorLayout>
      <EditorMain>
        <LiveEditNotice state={course.version_state} />
        <FormSection
          tone="content"
          icon={faFileLines}
          title="Details"
          description="How the course is named and linked."
        >
          <FormRow>
            <FormLabel htmlFor="course-title">Title</FormLabel>
            <TextInput
              id="course-title"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="course-slug">Slug</FormLabel>
            <TextInput
              id="course-slug"
              value={slug}
              disabled={isPublished}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSlug(e.target.value)
              }
            />
          </FormRow>
        </FormSection>

        <FormSection
          tone="feedback"
          icon={faBookOpen}
          title="Description"
          description="What students read about the course before starting it."
        >
          <RichTextEditor
            value={course.content}
            onChange={setContent}
            ariaLabel="Course description"
          />
        </FormSection>

        <FormSection
          tone="answers"
          icon={faImage}
          title="Images"
          description="The pictures students see for this course."
        >
          <FormRow>
            <FormLabel htmlFor="course-cover">Cover image</FormLabel>
            <FieldHint>
              The banner behind the title on the course page.
            </FieldHint>
            <MediaPicker
              id="course-cover"
              value={cover}
              onChange={setCover}
              onUploadingChange={setCoverUploading}
              alt="Course cover image"
              wide
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="course-thumbnail">Thumbnail</FormLabel>
            <FieldHint>
              Shown on course cards, and beside the description on the course
              page.
            </FieldHint>
            <MediaPicker
              id="course-thumbnail"
              value={thumbnail}
              onChange={setThumbnail}
              onUploadingChange={setThumbnailUploading}
              alt="Course thumbnail"
            />
          </FormRow>
        </FormSection>
      </EditorMain>

      <EditorPanel aria-label="Save and settings">
        <SaveGroup
          heading={<PanelLabel>Course</PanelLabel>}
          dirty={dirty}
          saving={updateMutation.isPending}
          saveDisabled={uploading || !trimmedTitle}
          onSave={handleSave}
          onCancel={() => confirmDiscard(onDone)}
        >
          {!trimmedTitle && <FieldHint>Give the course a title.</FieldHint>}
          {updateMutation.isError &&
            (editConflict(updateMutation.error) ? (
              <EditConflictNotice
                error={updateMutation.error}
                onReload={reload}
              />
            ) : (
              <ErrorText>
                {apiErrorDetail(updateMutation.error) ??
                  'Could not save changes. Please try again.'}
              </ErrorText>
            ))}
        </SaveGroup>

        <PanelGroup>
          <FormRow>
            <FormLabel htmlFor="course-status">Status</FormLabel>
            {isPublished ? (
              <InfoNotice>
                Published. Order, slug and publication are frozen for students
                on this version; change them in a new version.
              </InfoNotice>
            ) : isDraftVersion ? (
              <InfoNotice>
                This is a draft of a live course. Publish it from{' '}
                <Link
                  to="/courses/$courseId/$version/changes"
                  params={{
                    courseId: String(course.id),
                    version: String(course.version),
                  }}
                >
                  Review changes
                </Link>
                .
              </InfoNotice>
            ) : (
              <>
                <Select
                  id="course-status"
                  value={status}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setStatus(e.target.value as CourseAdmin['status'])
                  }
                >
                  <option value="draft">draft</option>
                  <option value="published">published</option>
                </Select>
                {willPublish && (
                  <WarningNotice>
                    Publishing is permanent — a published course can't be
                    returned to draft or reordered, since that would disrupt
                    students who are partway through the series. Its quizzes
                    lock once students can reach them: published questions can
                    only have their wording changed, and no questions can be
                    added.
                  </WarningNotice>
                )}
              </>
            )}
            <DraftAncestorNotice
              entityLabel="course"
              ancestors={[{ label: 'series', status: course.series_status }]}
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="course-order">Order</FormLabel>
            <TextInput
              id="course-order"
              type="number"
              value={order}
              disabled={isPublished}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOrder(e.target.value)
              }
            />
          </FormRow>
        </PanelGroup>
      </EditorPanel>

      <ConfirmDialog
        open={confirming}
        title="This can't be undone"
        confirmLabel="Publish"
        onConfirm={handleSave}
        onCancel={() => setConfirming(false)}
      >
        <p>
          A published course can't be returned to draft or reordered, since that
          would disrupt students who are partway through the series.
        </p>
        <p>
          Its quizzes lock once students can reach them: published questions can
          only have their wording changed, and no questions can be added.
        </p>
      </ConfirmDialog>
      {unsavedDialog}
    </EditorLayout>
  );
}

const Description = styled(RichContent)`
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const Empty = styled.p`
  color: var(--grey-600);
  margin: 0;
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const Images = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--component-margin);
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const CertificateName = styled.strong`
  color: var(--grey-900);
  font-weight: 600;
`;

const CertificateRow = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
  padding: var(--inner-padding);
`;

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
